import React, { useEffect, useState } from 'react';
import { Upload, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { BlogManager } from './BlogManager';

type SupportStatus = 'new' | 'in-progress' | 'resolved';
type SupportRecord = {
  key: string;
  submittedAt: string;
  productSlug: string | null;
  purchaseEmail: string | null;
  replyEmail: string;
  message: string;
  status: SupportStatus;
};

export const AdminPage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  const [signedIn, setSignedIn] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [logs, setLogs] = useState<{ id: string; message: string; timestamp: string; }[]>([]);
  const [supportRecords, setSupportRecords] = useState<SupportRecord[]>([]);
  const [supportLoading, setSupportLoading] = useState(false);

  const addLog = (message: string) => {
    const newLog = {
      id: crypto.randomUUID(),
      message,
      timestamp: new Date().toLocaleTimeString()
    };
    setLogs(prev => [...prev, newLog]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlam')) {
        setError('Only .xlam files are allowed');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setSuccess(false);
      addLog(`Selected file: ${selectedFile.name} (${(selectedFile.size / 1024).toFixed(2)} KB)`);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      setError(null);
      setSuccess(false);

      addLog('Starting upload process...');

      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
      });

      reader.readAsDataURL(file);
      const base64Data = await base64Promise;

      addLog('File converted to Base64');

      const response = await fetch('/.netlify/functions/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileData: base64Data,
          fileName: file.name
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `Upload failed with status ${response.status}`);
      }

      addLog(data.message || 'Upload successful');
      setSuccess(true);
      setFile(null);

      // Reset file input
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setError(message);
      addLog(`Error: ${message}`);
    } finally {
      setUploading(false);
    }
  };

  const loadSupportRecords = async () => {
    setSupportLoading(true);
    setError(null);
    try {
      const response = await fetch('/.netlify/functions/list-support', {
      });
      const data = await response.json();
      if (response.status === 401) setSignedIn(false);
      if (!response.ok) throw new Error(data.message || 'Could not load support requests');
      setSignedIn(true);
      setSupportRecords(data.records || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load support requests');
    } finally {
      setSupportLoading(false);
    }
  };

  const updateSupportStatus = async (key: string, status: SupportStatus) => {
    setError(null);
    const response = await fetch('/.netlify/functions/update-support', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, status }),
    });
    const data = await response.json();
    if (!response.ok) {
      setError(data.message || 'Could not update support request');
      return;
    }
    setSupportRecords((records) => records.map((record) => record.key === key ? { ...record, status } : record));
  };

  const signIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setSigningIn(true);
    setError(null);
    try {
      const response = await fetch('/.netlify/functions/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: adminToken }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not sign in');
      setAdminToken('');
      setSignedIn(true);
      await loadSupportRecords();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in');
    } finally {
      setSigningIn(false);
    }
  };

  const signOut = async () => {
    await fetch('/.netlify/functions/admin-logout', { method: 'POST' });
    setSignedIn(false);
    setSupportRecords([]);
  };

  useEffect(() => {
    loadSupportRecords();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          </div>
          
          <div className="space-y-6">
            {!signedIn ? (
              <form onSubmit={signIn} className="rounded-lg border bg-gray-50 p-4">
                <label htmlFor="admin-token" className="block text-sm font-medium text-gray-700 mb-2">Administrator sign-in</label>
                <div className="flex gap-3">
                  <input id="admin-token" type="password" value={adminToken} onChange={(event) => setAdminToken(event.target.value)} autoComplete="current-password" className="block flex-1 rounded-md border border-gray-300 px-3 py-2" />
                  <button type="submit" disabled={!adminToken || signingIn} className="rounded-md bg-gray-900 px-4 py-2 text-white disabled:opacity-50">{signingIn ? 'Signing in...' : 'Sign in'}</button>
                </div>
                <p className="mt-2 text-sm text-gray-600">Sign in once on this device. Your secure session lasts for 30 days.</p>
              </form>
            ) : (
              <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4">
                <p className="text-sm font-medium text-green-800">Administrator signed in</p>
                <button onClick={signOut} className="text-sm text-green-800 underline">Sign out</button>
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold mb-4">Upload Excel Add-in</h2>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="flex flex-col items-center">
                  <Upload className="h-12 w-12 text-gray-400 mb-4" />
                  <label className="block w-full">
                    <span className="sr-only">Choose file</span>
                    <input 
                      type="file" 
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-full file:border-0
                        file:text-sm file:font-semibold
                        file:bg-green-50 file:text-green-700
                        hover:file:bg-green-100"
                      accept=".xlam"
                      onChange={handleFileChange}
                      disabled={uploading}
                    />
                  </label>
                  {file && (
                    <div className="mt-4 text-sm text-gray-600">
                      Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                    </div>
                  )}
                </div>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-4">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-4">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <p className="text-green-700">File uploaded successfully!</p>
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={!file || !signedIn || uploading}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                (!file || !signedIn || uploading) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {uploading ? 'Uploading...' : 'Upload Add-in'}
            </button>

            <div className="border-t pt-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Purchaser Support</h2>
                  <p className="text-sm text-gray-600">Private download problems submitted by verified purchasers.</p>
                </div>
                <button onClick={loadSupportRecords} disabled={!signedIn || supportLoading} className="inline-flex items-center px-4 py-2 rounded-md bg-gray-900 text-white disabled:opacity-50">
                  <RefreshCw className={`h-4 w-4 mr-2 ${supportLoading ? 'animate-spin' : ''}`} />
                  Load requests
                </button>
              </div>
              <div className="space-y-4">
                {supportRecords.map((record) => (
                  <article key={record.key} className="border rounded-lg p-4">
                    <div className="flex flex-wrap justify-between gap-3 mb-3">
                      <div>
                        <p className="font-medium">{record.replyEmail}</p>
                        <p className="text-xs text-gray-500">{new Date(record.submittedAt).toLocaleString()} · {record.productSlug || 'Unknown product'}</p>
                      </div>
                      <select value={record.status} onChange={(event) => updateSupportStatus(record.key, event.target.value as SupportStatus)} className="border rounded-md px-2 py-1 text-sm">
                        <option value="new">New</option>
                        <option value="in-progress">In progress</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-gray-800">{record.message}</p>
                    <a href={`mailto:${encodeURIComponent(record.replyEmail)}?subject=${encodeURIComponent('How Do You Excel download support')}`} className="inline-block mt-3 text-green-700 hover:underline">Reply by email</a>
                  </article>
                ))}
                {!supportLoading && supportRecords.length === 0 && <p className="text-sm text-gray-500">{signedIn ? 'No support requests found.' : 'Sign in to view purchaser support requests.'}</p>}
              </div>
            </div>

            <BlogManager signedIn={signedIn} />

            {logs.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-semibold mb-2">Upload Log</h3>
                <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-auto max-h-60">
                  {logs.map((log) => (
                    <div key={log.id} className="whitespace-pre-wrap">
                      [{log.timestamp}] {log.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
